<template>
  <section>
    <configurator v-for="recommended in products"
                  v-if="recommended.custom_options && recommended.custom_options.length > 0"
                  :product="recommended"
                  :key="recommended.entity_id"
                  :category-id="0"
    />
    <section class="py20 new-collection container px15">
      <div>
        <header class="col-md-12">
          <h4 class="align-left cl-accent" :class="{loading: loading, disabled: !loading && (!products || products.length === 0)}">
            {{ heading }}
          </h4>
        </header>
      </div>
      <div class="row center-xs">
        <product-listing-related v-if="products && products.length > 0 && !loading" columns="4" :products="products" />
        <div v-else-if="loading" class="product-listing-skeleton__root">
          <div
            v-for="index in 6"
            :key="index"
            class="product-listing-skeleton__item"
          >
            <img src="/assets/placeholder.svg" alt="product" width="179" height="179" loading="eager" fetchpriority="high">
            <span class="title mb20 serif my10 center-xs start-md" />
            <p class="description">
              <span style="width: 60%;" />
              <span style="width: 75%;" />
            </p>
          </div>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import config from 'config'
import { mapGetters, mapActions } from 'vuex';
import ProductListingRelated from "../../ProductListingRelated";
import Configurator from 'theme/components/theme/blocks/Home/ProductTileGrid/Configurator'

export default {
  name: 'Recommendations',
  components: {
    ProductListingRelated,
    Configurator
  },
  data: () => ({
    relatedCategory: null,
    loading: true
  }),
  async beforeMount () {
    this.$bus.$on('product-after-load', this.refreshList)

    if (config.usePriceTiers) {
      this.$bus.$on('user-after-loggedin', this.refreshList)
      this.$bus.$on('user-after-logout', this.refreshList)
    }

    await this.refreshList()
  },
  beforeDestroy () {
    if (config.usePriceTiers) {
      this.$bus.$off('user-after-loggedin', this.refreshList)
      this.$bus.$off('user-after-logout', this.refreshList)
    }
  },
  destroyed () {
    this.$bus.$off('product-after-load', this.refreshList)
  },
  methods: {
    ...mapActions({
      loadRecommendations: 'product/loadRecommendations'
    }),
    async refreshList () {
      this.loading = true;
      let payload = {
        productId: this.product.current.entity_id
      }
      if (!this.currentPartner.active || !this.currentPartner.enabled) {
        // Whole partner is unavailable - same-partner alternatives would be useless, so this
        // takes priority over the single-product check below.
        payload.type = 'disabled_partner'
        payload.indexSuffix = `${this.$route.params.store}?location_code=${this.$route.params.locationCode}&tags=${this.currentPartner.tags.join(',')}`
      } else if (this.product.current.status === 2) {
        // Just this product is discontinued (matches Product.js's isDisabledProduct check) -
        // deliberately request same-partner related products via the 'discontinued' type,
        // instead of relying on the fbt-empty-result fallback chain (loadRecommendations()
        // falls back to 'related' when fbt returns nothing, for any reason, not just this one -
        // the fallback happened to be same-partner too, but for the wrong reason, and always
        // labels itself 'related' regardless of why, showing "More like this" instead of the
        // more accurate "Try these instead" heading this case has).
        payload.type = 'discontinued'
        // Same URL shape loadRecommendations()'s own fbt->related fallback builds: partner
        // code as the path segment, city as a query param (not a combined "{location}_{partner}"
        // string - partner codes can contain underscores, so that couldn't be split back apart
        // server-side).
        payload.indexSuffix = `${this.$route.params.store}?location_code=${this.$route.params.locationCode}`
      }
      await this.loadRecommendations(payload)
        .finally(() => {
          this.loading = false;
        })
    }
  },
  computed: {
    ...mapGetters({
      recommendedProducts: 'product/getProductRecommendations',
      currentPartner: 'partners/getCurrentPartner'
    }),
    recommendation () {
      return this.recommendedProducts(this.product.current.entity_id)
    },
    products () {
      if (this.recommendation) {
        return this.recommendation.items || []
      }
      return []
    },
    product () {
      return this.$store.state.product
    },
    heading () {
      let mapping = {
        'fbt': this.$t('Goes Well With'),
        'related': this.$t('More like this'),
        'discontinued': this.$t('Try these instead'),
        'disabled_partner': this.$t('Different partner to try')
      }
      if (this.recommendation) {
        return mapping[this.recommendation.type] || ''
      }
      return ''
    }
  }
}
</script>

<style scoped lang="scss">
.new-collection {
  display: flex;
  flex-direction: column;
}

.name-category{
  color: #11bb2e;
  text-transform: lowercase;
  border-bottom: 1px solid #11bb2e;
}

h4 {
  &.loading {
    min-height: 22px;
    display: inline-block;
    width: 240px;
    background: #F0F0F0;
    border-radius: 6px;
    float: inline-start;

    @media only screen and (max-width: 768px) {
      min-height: 19px;
    }
  }

  &.disabled {
    display: none;
  }
}

.product-listing-skeleton {
  &__root {
    display: flex;
    width: 100%;
    overflow: hidden;
    padding: 0 8px;
    column-gap: 20px;
  }

  &__item {
    width: 100%;
    max-width: 195px;
    height: 290px;

    .title {
      display: inline-block;
      width: 50%;
      height: 22px;
      background: #F0F0F0;
      border-radius: 6px;
      float: inline-start;
    }

    .description {
      float: inline-start;
      width: 100%;
      display: flex;
      flex-direction: column;
      row-gap: 12px;
      margin-top: 0;

      span {
        display: block;
        height: 18px;
        background: #F0F0F0;
        border-radius: 6px;
        float: inline-start;
      }
    }
  }

  @media only screen and (max-width: 1200px) {
    &__item {
      max-width: 225px;
      height: 340px;

      img {
        width: 225px;
        height: 225px;
      }
    }
  }

  @media only screen and (max-width: 991px) {
    &__item {
      max-width: 169px;
      height: 288px;

      img {
        width: 169px;
        height: 169px;
      }
    }
  }

  @media only screen and (max-width: 768px) {
    &__item {
      max-width: 121px;
      height: 220px;

      &:nth-child(5),
      &:nth-child(6) {
        display: none;
      }

      img {
        width: 100%;
        height: 121px;
      }

      .title {
        height: 19px;
        width: 80%;
        float: none;
        margin-bottom: 16px;
      }

      .description {
        row-gap: 8px;
      }
    }
  }

  @media only screen and (max-width: 500px) {
    &__item {
      max-width: 220px;
      height: auto;

      &:nth-child(3),
      &:nth-child(4) {
        display: none;
      }
    }
  }
}
</style>
